import { DateTime } from '@cheqroom/date-time';
import {
	Archive,
	Barcode,
	Calendar,
	Categories,
	Contacts,
	Database,
	Deprecation,
	DotsHorizontal,
	FlagAlt,
	Kits,
	Marker,
	Number,
	Qr,
	RfidIcon,
	RoundedSquare,
} from '@cheqroom/icons';
import { FC } from 'react';

import { DateFormatter } from '../../util';
import { getItemTypesFromUser } from '../item-types/use-item-types';
import { ItemFields } from './python.hooks';
import { FieldType, SystemFieldType, VirtualFieldType } from './types';

/**
 * Extracts the root location ID from a polymorphic rootLocation field.
 * The rootLocation can be either:
 * - An object with an `_id` property
 * - A string (the ID itself)
 * - undefined/null
 */
export const extractRootLocationId = (
	rootLocation: { _id?: string } | string | undefined | null
): string | undefined => {
	if (!rootLocation) {
		return undefined;
	}

	if (typeof rootLocation === 'object') {
		if ('_id' in rootLocation) {
			return rootLocation._id;
		}
	} else if (typeof rootLocation === 'string') {
		return rootLocation;
	}

	return undefined;
};

export const getIconForField = (fieldId: string): FC | null => {
	if (fieldId.startsWith('fields.')) return null;

	switch (fieldId) {
		case SystemFieldType.barcode:
			return Barcode;
		case SystemFieldType.qr:
			return Qr;
		case SystemFieldType.rfid:
			return RfidIcon;
		case SystemFieldType.purchaseDate:
		case SystemFieldType.warrantyDate:
		case SystemFieldType.created:
		case SystemFieldType.modified:
			return Calendar;
		case SystemFieldType.address:
			return Marker;
		case SystemFieldType.location:
		case VirtualFieldType.sublocation:
			return undefined; // Icons handled in custom rendering with location-specific icons
		case SystemFieldType.purchasePrice:
		case SystemFieldType.residualValue:
			return Deprecation;
		case SystemFieldType.flag:
			return FlagAlt;
		case SystemFieldType.contact:
			return Contacts;
		case SystemFieldType.category:
			return Categories;
		case SystemFieldType.kit:
			return Kits;
		case SystemFieldType.expired:
			return Archive;
		case SystemFieldType.brand:
		case SystemFieldType.model:
			return RoundedSquare;
		case SystemFieldType.quantity:
			return Number;
		// Ideally we would render the item type icon here, but we don't have access to the item type
		// in the item fields context.
		case SystemFieldType.itemTypeId:
			return Database;
		default:
			return DotsHorizontal;
	}
};

export const getItemFieldValue = (itemFields: ItemFields, fieldId: string) => {
	const itemTypes = getItemTypesFromUser();

	if (fieldId === SystemFieldType.contact && itemFields.status !== 'available') {
		switch (itemFields.status) {
			case 'checkedout':
				return itemFields.order?.customer?.name;
			case 'in_custody':
				return itemFields.custody?.name;
		}
	}

	if (fieldId === SystemFieldType.flag) {
		return itemFields.flag?.name;
	}

	if (fieldId === SystemFieldType.itemTypeId) {
		const itemType = itemTypes.find(({ id }) => id === itemFields.itemTypeId);
		return itemType?.name_plural;
	}

	if (fieldId === SystemFieldType.barcode) {
		return itemFields.barcodes?.[0];
	}

	if (fieldId === SystemFieldType.qr) {
		return itemFields.codes?.[0];
	}

	if (fieldId === SystemFieldType.rfid) {
		return (itemFields.rfidcodes as string[] | undefined)?.[0];
	}

	if (fieldId.startsWith('fields.')) {
		const customFieldKey = fieldId.replace('fields.', '');
		return itemFields.fields[customFieldKey] as unknown | undefined;
	}

	// Handle location field - return root location data with full path for tooltip
	if (fieldId === SystemFieldType.location) {
		const locationPath = itemFields.locationPath;
		const location = itemFields.location;

		if (!locationPath || locationPath.length === 0) {
			return location ? { _id: location._id, name: location.name, icon: location.icon } : undefined;
		}

		// Find root location in path
		const rootLocationId = extractRootLocationId(location?.rootLocation);

		// Root is either the location with rootLocationId or the first item
		const root = rootLocationId ? locationPath.find((loc) => loc._id === rootLocationId) : locationPath[0];

		return root ? { _id: root._id, name: root.name, icon: root.icon, fullPath: locationPath } : undefined;
	}

	// Handle virtual sublocation field - return full location path (including root)
	// The component will filter out the root (first element) for display but keep it in the tooltip
	if (fieldId === VirtualFieldType.sublocation) {
		const locationPath = (itemFields as { locationPath?: Array<{ _id: string; name: string; icon?: string }> })
			.locationPath;

		if (!locationPath || locationPath.length === 0) {
			return undefined;
		}

		const rootLocationId = extractRootLocationId(itemFields.location?.rootLocation);

		// Filter out the root location to get only sublocations
		const sublocations = rootLocationId
			? locationPath.filter((loc) => loc._id !== rootLocationId)
			: locationPath.slice(1);

		// Return undefined if there are no actual sublocations (only root exists)
		if (sublocations.length === 0) {
			return undefined;
		}

		// Return full path - root is first element, component will filter it for display
		return locationPath;
	}

	const subObjects = [SystemFieldType.kit, SystemFieldType.category];
	const fieldType = subObjects.find((type) => fieldId === type || fieldId.startsWith(`${type}.`));
	if (fieldType) {
		if (fieldType === fieldId) {
			const field = itemFields[fieldType] as { name?: string } | undefined;
			if (field) {
				return field?.name;
			}
		} else {
			const subFieldKey = fieldId.replace(`${fieldType}.`, '');
			const subObject = itemFields[fieldType] as { [key: string]: string } | undefined;
			if (subObject && subObject[subFieldKey]) {
				return subObject[subFieldKey];
			}
		}
	}

	if (
		[
			SystemFieldType.created,
			SystemFieldType.purchaseDate,
			SystemFieldType.warrantyDate,
			SystemFieldType.modified,
		].includes(fieldId as SystemFieldType)
	) {
		// Depending on the origin, the date can be an ISO string or a moment object
		const dateValue = itemFields[fieldId] as string | { toISOString: () => string };
		if (!dateValue) return;

		const dateString = typeof dateValue === 'object' ? dateValue.toISOString() : dateValue;
		return DateFormatter.shortDate(DateTime.fromISOString(dateString));
	}

	return itemFields[fieldId] as unknown | undefined;
};

export const mapItemFieldIdToFieldId = (itemFieldId: FieldType) => {
	if (itemFieldId === SystemFieldType.barcode) return 'barcodes';
	if (itemFieldId === SystemFieldType.qr) return 'codes';
	if (itemFieldId === SystemFieldType.rfid) return 'rfidcodes';
	if (itemFieldId === SystemFieldType.kit) return 'kit.name';
	if (itemFieldId === SystemFieldType.category) return 'category.name';
	if (itemFieldId === SystemFieldType.location)
		return 'location.name,location.icon,location.rootLocation,locationPath,locationPath.name,locationPath.icon';
	if (itemFieldId === VirtualFieldType.sublocation)
		return 'locationPath,locationPath.name,locationPath.icon,location.rootLocation';
	return itemFieldId;
};
